import { demoAuditLogs, demoCustomers, demoFollowUps, demoOpportunities, demoProducts, demoQuotes, demoReminders, demoSamples, demoUsage } from "./demo";
import { apiBlob, apiRequest } from "../lib/apiClient";
import { ApiError } from "../lib/errors";
import { expireSession, sessionExpiredError } from "../lib/sessionLifecycle";
import { isSupabaseConfigured, supabase } from "../lib/supabase";
import { buildCustomerTimeline } from "../lib/timeline";
import { getAllowedTransitions } from "../lib/sampleWorkflow";
import { defaultCrmSettings, settingsFromPayload, settingsToPayload } from "../lib/settings";
import type { Attachment, AuditLog, CrmSettings, Customer, CustomerInput, CustomerListItem, CustomerListQuery, CustomerListResult, DashboardBreakdown, DashboardSnapshot, FollowUp, Opportunity, OpportunityInput, Product, ProductInput, ProductListQuery, ProductListResult, ProductVersion, Profile, Quote, QuoteInput, Reminder, ReportSnapshot, Sample, SampleDiscussion, SampleInput, SampleListQuery, SampleListResult, SampleWorkflowEvent, SampleWorkflowStatus, TimelineEvent, UsageMetrics } from "../types/crm";

const STORAGE_KEY = "yky-crm-demo-customers-v1";
const FOLLOW_UP_KEY = "yky-crm-demo-followups-v1";
const SAMPLE_KEY = "yky-crm-demo-samples-v1";
const OPPORTUNITY_KEY = "yky-crm-demo-opportunities-v1";
const QUOTE_KEY = "yky-crm-demo-quotes-v1";
const REMINDER_KEY = "yky-crm-demo-reminders-v1";
const AUDIT_KEY = "yky-crm-demo-audit-v1";
const ATTACHMENT_KEY = "yky-crm-demo-attachments-v1";
const SETTINGS_KEY = "yky-crm-demo-settings-v1";
const PRODUCT_KEY = "yky-crm-demo-products-v1";
const SAMPLE_EVENT_KEY = "yky-crm-demo-sample-events-v1";
const SAMPLE_DISCUSSION_KEY = "yky-crm-demo-sample-discussions-v1";
const DELETED_CUSTOMER_KEY = "yky-crm-demo-deleted-customers-v1";

function dataError(error: unknown, userMessage: string, code = "DATA_REQUEST_FAILED") {
  const details = error as { message?: string; code?: string; status?: number } | null;
  return new ApiError({
    code: details?.code || code,
    userMessage,
    technicalMessage: details?.message || "Unknown Supabase data error",
    status: details?.status,
    retryable: true,
    cause: error,
  });
}

function loadLocal<T>(key: string, fallback: T): T {
  if (typeof window === "undefined") return fallback;
  const stored = window.localStorage.getItem(key);
  if (!stored) return structuredClone(fallback);
  try {
    return JSON.parse(stored) as T;
  } catch {
    return structuredClone(fallback);
  }
}

function saveLocal<T>(key: string, value: T) {
  if (typeof window !== "undefined") window.localStorage.setItem(key, JSON.stringify(value));
}

const crmSettingKeys = ["company_profile", "follow_up_defaults", "email_signature", "lead_scoring", "prospect_scoring", "whatsapp_templates", "customer_sources", "product_interests"];

export async function getCrmSettings(): Promise<CrmSettings> {
  if (!isSupabaseConfigured || !supabase) return loadLocal(SETTINGS_KEY, structuredClone(defaultCrmSettings));
  const { data, error } = await supabase.from("app_settings").select("key,value").in("key", crmSettingKeys);
  if (error) throw dataError(error, "系统设置暂时无法加载，请稍后重试", "SETTINGS_LOAD_FAILED");
  const payload = Object.fromEntries((data ?? []).map((row) => [row.key, row.value]));
  return settingsFromPayload(payload);
}

export async function updateCrmSettings(settings: CrmSettings): Promise<CrmSettings> {
  const payload = settingsToPayload(settings);
  if (!isSupabaseConfigured || !supabase) {
    const next = settingsFromPayload(payload);
    saveLocal(SETTINGS_KEY, next);
    return next;
  }
  const { data, error } = await supabase.rpc("update_crm_settings", { p_settings: payload });
  if (error) throw dataError(error, "系统设置保存失败，请检查权限和填写内容", "SETTINGS_SAVE_FAILED");
  return settingsFromPayload((data || {}) as Record<string, unknown>);
}

function loadDemoCustomers(): Customer[] {
  const stored = loadLocal(STORAGE_KEY, demoCustomers) as Array<Partial<Customer> & Pick<Customer, "id">>;
  const defaults: Pick<Customer, "lifecycle_status" | "development_stage" | "source_detail" | "product_interest" | "estimated_quantity" | "estimated_value" | "lead_score" | "lead_score_override" | "converted_at" | "converted_by"> = { lifecycle_status: "customer", development_stage: "new_lead", source_detail: null, product_interest: null, estimated_quantity: null, estimated_value: null, lead_score: 0, lead_score_override: false, converted_at: null, converted_by: null };
  const normalized = stored.map((customer) => ({ ...defaults, ...demoCustomers.find((item) => item.id === customer.id), ...customer }) as Customer);
  const ids = new Set(normalized.map((customer) => customer.id));
  const deleted = new Set(loadLocal<string[]>(DELETED_CUSTOMER_KEY, []));
  return [...normalized, ...demoCustomers.filter((customer) => !ids.has(customer.id))].filter((customer) => !deleted.has(customer.id));
}

function loadDemoSamples(): Sample[] {
  const stored = loadLocal(SAMPLE_KEY, demoSamples) as Array<Partial<Sample> & Pick<Sample, "id" | "customer_id" | "product_name">>;
  const legacyStatus: Record<string, Sample["status"]> = { requested: "draft", making: "in_production", ready: "ready_to_ship", received: "delivered", feedback: "awaiting_customer_feedback" };
  return stored.map((sample, index) => {
    const seed = demoSamples.find((item) => item.id === sample.id);
    const status = legacyStatus[String(sample.status)] || sample.status || seed?.status || "draft";
    return { sample_no: `YKY-S-DEMO-${String(index + 1).padStart(3, "0")}`, product_id: null, version_no: 1, parent_sample_id: null, priority: "normal", current_assignee: null, target_market: null, expected_ship_date: null, target_order_quantity: null, target_unit_price: null, submitted_at: null, accepted_at: null, started_at: null, completed_at: null, due_at: null, ...seed, ...sample, status, current_department: departmentForSampleStatus(status) } as Sample;
  });
}

function cleanInput<T extends object>(input: T): Partial<T> {
  return Object.fromEntries(
    Object.entries(input).filter(([, value]) => value !== undefined).map(([key, value]) => [key, value === "" ? null : value]),
  ) as Partial<T>;
}

function appendDemoCustomerAudit(before: Customer, after: Customer, occurredAt: string) {
  const rows = loadLocal(AUDIT_KEY, demoAuditLogs);
  if (before.development_stage !== after.development_stage || before.status !== after.status) {
    const from = before.development_stage !== after.development_stage ? before.development_stage : before.status;
    const to = before.development_stage !== after.development_stage ? after.development_stage : after.status;
    rows.unshift({ id: crypto.randomUUID(), actor_id: "demo-user", actor_name: "演示管理员", action: "customer.stage_change", entity_type: "customer", entity_id: after.id, customer_id: after.id, details: { from, to }, created_at: occurredAt });
  }
  if (before.assigned_to !== after.assigned_to) {
    rows.unshift({ id: crypto.randomUUID(), actor_id: "demo-user", actor_name: "演示管理员", action: "customer.owner_change", entity_type: "customer", entity_id: after.id, customer_id: after.id, details: { from: before.assigned_to, to: after.assigned_to }, created_at: occurredAt });
  }
  saveLocal(AUDIT_KEY, rows);
}

export async function listCustomers(): Promise<Customer[]> {
  if (!isSupabaseConfigured || !supabase) return loadDemoCustomers();
  const { data, error } = await supabase
    .from("customers")
    .select("*")
    .is("deleted_at", null)
    .order("updated_at", { ascending: false })
    .limit(500);
  if (error) throw dataError(error, "客户数据暂时无法加载，请稍后重试");
  return (data ?? []) as Customer[];
}

function demoBreakdown(values: Array<string | null | undefined>): DashboardBreakdown[] {
  const counts = new Map<string, number>();
  values.forEach((value) => counts.set(value?.trim() || "未知", (counts.get(value?.trim() || "未知") || 0) + 1));
  return [...counts].map(([label, count]) => ({ label, count })).sort((left, right) => right.count - left.count);
}

function shanghaiDay(value: string | Date) {
  return new Intl.DateTimeFormat("en-CA", { timeZone: "Asia/Shanghai", year: "numeric", month: "2-digit", day: "2-digit" }).format(new Date(value));
}

export async function getDashboardSnapshot(): Promise<DashboardSnapshot> {
  if (isSupabaseConfigured && supabase) {
    const { data, error } = await supabase.rpc("dashboard_snapshot");
    if (error) throw dataError(error, "Dashboard 数据暂时无法加载，请稍后重试");
    return data as DashboardSnapshot;
  }
  const customers = loadDemoCustomers();
  const reminders = loadLocal(REMINDER_KEY, demoReminders);
  const samples = loadDemoSamples();
  const opportunities = loadLocal(OPPORTUNITY_KEY, demoOpportunities);
  const quotes = loadLocal(QUOTE_KEY, demoQuotes);
  const followUps = loadLocal(FOLLOW_UP_KEY, demoFollowUps);
  const today = shanghaiDay(new Date());
  const pipelineMap = new Map<string, { count: number; amount: number }>();
  opportunities.forEach((item) => { const current = pipelineMap.get(item.stage) || { count: 0, amount: 0 }; pipelineMap.set(item.stage, { count: current.count + 1, amount: current.amount + (item.amount || 0) }); });
  const sampleStatuses = demoBreakdown(samples.map((item) => item.status));
  const quoteStatuses = demoBreakdown(quotes.map((item) => item.status)).map((item) => ({ ...item, amount: quotes.filter((quote) => quote.status === item.label).reduce((sum, quote) => sum + quote.total_amount, 0) }));
  const recentActivity = followUps.filter((item) => item.channel === "email" || item.channel === "whatsapp").map((item) => { const customer = customers.find((row) => row.id === item.customer_id); return { id: item.id, customer_id: item.customer_id, channel: item.channel as "email" | "whatsapp", subject: item.subject, content: item.content, occurred_at: item.occurred_at, company_name: customer?.company_name || null, contact_name: customer?.contact_name || null }; }).sort((left, right) => new Date(right.occurred_at).getTime() - new Date(left.occurred_at).getTime()).slice(0, 8);
  return {
    metrics: {
      customer_count: customers.length,
      new_inquiries: customers.filter((item) => shanghaiDay(item.created_at) === today).length,
      today_follow_ups: reminders.filter((item) => !item.completed_at && shanghaiDay(item.due_at) === today).length,
      overdue: reminders.filter((item) => !item.completed_at && new Date(item.due_at).getTime() < Date.now()).length,
      high_intent: customers.filter((item) => item.lead_score >= 70 || item.grade === "A").length,
      active_samples: samples.filter((item) => !["closed", "cancelled"].includes(item.status)).length,
      pending_engineering: samples.filter((item) => ["submitted_to_engineering", "business_resubmitted"].includes(item.status)).length,
      engineering_overdue: samples.filter((item) => ["submitted_to_engineering", "engineering_review", "business_resubmitted"].includes(item.status) && Boolean(item.due_at) && new Date(item.due_at!).getTime() < Date.now()).length,
      production_samples: samples.filter((item) => ["production_queued", "scheduled", "in_production"].includes(item.status)).length,
      production_exceptions: samples.filter((item) => ["production_exception", "qc_failed"].includes(item.status)).length,
      awaiting_feedback: samples.filter((item) => ["delivered", "awaiting_customer_feedback"].includes(item.status)).length,
      quote_amount: quotes.filter((item) => ["sent", "accepted"].includes(item.status)).reduce((sum, item) => sum + item.total_amount, 0),
      pipeline_amount: opportunities.filter((item) => !["won", "lost"].includes(item.stage)).reduce((sum, item) => sum + (item.amount || 0), 0),
    },
    pipeline: [...pipelineMap].map(([stage, value]) => ({ stage, ...value })),
    sources: demoBreakdown(customers.map((item) => item.source_detail || item.lead_source)).slice(0, 8),
    sample_statuses: sampleStatuses,
    quote_statuses: quoteStatuses,
    today_tasks: reminders.filter((item) => !item.completed_at && new Date(item.due_at).getTime() < Date.now() + 86400000).sort((left, right) => new Date(left.due_at).getTime() - new Date(right.due_at).getTime()).slice(0, 8).map((item) => ({ id: item.id, customer_id: item.customer_id, title: item.title, due_at: item.due_at, completed_at: item.completed_at, company_name: item.customer?.company_name || null, contact_name: item.customer?.contact_name || null })),
    key_customers: customers.filter((item) => item.is_key_account).sort((left, right) => new Date(right.updated_at).getTime() - new Date(left.updated_at).getTime()).slice(0, 6),
    recent_activity: recentActivity,
    stale_customers: customers.filter((item) => !item.last_follow_up_at || Date.now() - new Date(item.last_follow_up_at).getTime() > 14 * 86400000).sort((left, right) => (left.last_follow_up_at ? new Date(left.last_follow_up_at).getTime() : 0) - (right.last_follow_up_at ? new Date(right.last_follow_up_at).getTime() : 0)).slice(0, 6),
  };
}

export async function getReportSnapshot(from: string, to: string): Promise<ReportSnapshot> {
  if (isSupabaseConfigured && supabase) {
    const { data, error } = await supabase.rpc("crm_report_snapshot", { p_from: from, p_to: to });
    if (error) throw dataError(error, "数据分析暂时无法加载，请稍后重试");
    return data as ReportSnapshot;
  }
  const fromTime = new Date(from).getTime(); const toTime = new Date(to).getTime();
  const customers = loadDemoCustomers(); const reminders = loadLocal(REMINDER_KEY, demoReminders); const samples = loadDemoSamples(); const opportunities = loadLocal(OPPORTUNITY_KEY, demoOpportunities); const quotes = loadLocal(QUOTE_KEY, demoQuotes); const followUps = loadLocal(FOLLOW_UP_KEY, demoFollowUps);
  const rangeCustomers = customers.filter((item) => { const time = new Date(item.created_at).getTime(); return time >= fromTime && time < toTime; });
  const grouped = (values: Array<string | null | undefined>) => demoBreakdown(values).map((item) => ({ label: item.label || "未知", count: item.count }));
  const sampleCustomerIds = new Set(samples.filter((item) => { const time = new Date(item.created_at).getTime(); return time >= fromTime && time < toTime; }).map((item) => item.customer_id));
  const quoteCustomerIds = new Set(quotes.filter((item) => { const time = new Date(item.created_at).getTime(); return time >= fromTime && time < toTime; }).map((item) => item.customer_id));
  const firstResponses = rangeCustomers.flatMap((customer) => { const followUp = followUps.filter((item) => item.customer_id === customer.id).sort((a, b) => new Date(a.occurred_at).getTime() - new Date(b.occurred_at).getTime())[0]; if (!followUp) return []; const hours = (new Date(followUp.occurred_at).getTime() - new Date(customer.created_at).getTime()) / 3600000; return hours >= 0 ? [hours] : []; });
  const rangeSamples = samples.filter((item) => { const time = new Date(item.created_at).getTime(); return time >= fromTime && time < toTime; });
  const rangeReminders = reminders.filter((item) => { const time = new Date(item.due_at).getTime(); return time >= fromTime && time < toTime; });
  const rangeFollowUps = followUps.filter((item) => { const time = new Date(item.occurred_at).getTime(); return time >= fromTime && time < toTime; });
  const averageDuration = (values: number[], divisor: number) => values.length ? Math.round(10 * values.reduce((sum, value) => sum + value, 0) / values.length / divisor) / 10 : 0;
  const engineeringDurations = rangeSamples.flatMap((item) => item.submitted_at && item.accepted_at ? [new Date(item.accepted_at).getTime() - new Date(item.submitted_at).getTime()] : []);
  const productionDurations = rangeSamples.flatMap((item) => item.started_at && item.completed_at ? [new Date(item.completed_at).getTime() - new Date(item.started_at).getTime()] : []);
  const feedbackDurations = rangeSamples.flatMap((item) => item.delivered_at && ["feedback_received", "needs_adjustment", "needs_new_version", "customer_approved", "closed"].includes(item.status) ? [new Date(item.updated_at).getTime() - new Date(item.delivered_at).getTime()] : []);
  const salespersonMap = new Map<string, { name: string; customer_count: number; follow_up_count: number; completed_task_count: number; overdue_task_count: number; won_amount: number }>();
  customers.forEach((customer) => { const key = customer.assigned_to || "unassigned"; const current = salespersonMap.get(key) || { name: key === "demo-user" ? "演示管理员" : key === "demo-sales" ? "示例业务员" : "未分配", customer_count: 0, follow_up_count: 0, completed_task_count: 0, overdue_task_count: 0, won_amount: 0 }; const customerReminders = rangeReminders.filter((item) => item.customer_id === customer.id); current.customer_count += 1; current.follow_up_count += rangeFollowUps.filter((item) => item.customer_id === customer.id).length; current.completed_task_count += customerReminders.filter((item) => Boolean(item.completed_at)).length; current.overdue_task_count += customerReminders.filter((item) => !item.completed_at && new Date(item.due_at).getTime() < Date.now()).length; current.won_amount += opportunities.filter((item) => item.customer_id === customer.id && item.stage === "won" && new Date(item.updated_at).getTime() >= fromTime && new Date(item.updated_at).getTime() < toTime).reduce((sum, item) => sum + (item.amount || 0), 0); salespersonMap.set(key, current); });
  return {
    metrics: { new_customers: rangeCustomers.length, lead_development: rangeCustomers.filter((item) => item.lifecycle_status === "prospect").length, overdue_follow_ups: reminders.filter((item) => !item.completed_at && new Date(item.due_at).getTime() < Date.now()).length, sample_to_quote_rate: sampleCustomerIds.size ? Math.round(1000 * [...sampleCustomerIds].filter((id) => quoteCustomerIds.has(id)).length / sampleCustomerIds.size) / 10 : 0, quote_to_won_rate: quoteCustomerIds.size ? Math.round(1000 * [...quoteCustomerIds].filter((id) => opportunities.some((item) => item.customer_id === id && item.stage === "won")).length / quoteCustomerIds.size) / 10 : 0, first_response_hours: firstResponses.length ? Math.round(10 * firstResponses.reduce((sum, value) => sum + value, 0) / firstResponses.length) / 10 : 0, deal_cycle_days: 0, follow_up_completion_rate: rangeReminders.length ? Math.round(1000 * rangeReminders.filter((item) => Boolean(item.completed_at)).length / rangeReminders.length) / 10 : 0, sample_engineering_hours: averageDuration(engineeringDurations, 3600000), sample_production_days: averageDuration(productionDurations, 86400000), sample_feedback_days: averageDuration(feedbackDurations, 86400000) },
    new_customer_trend: grouped(rangeCustomers.map((item) => shanghaiDay(item.created_at))).map((item) => ({ day: item.label, count: item.count })).sort((left, right) => left.day.localeCompare(right.day)),
    sources: grouped(rangeCustomers.map((item) => item.source_detail || item.lead_source)), countries: grouped(rangeCustomers.map((item) => item.country)), products: grouped(rangeCustomers.map((item) => item.product_interest)), funnel: grouped(customers.map((item) => item.development_stage)), sample_statuses: grouped(rangeSamples.map((item) => item.status)), communication_channels: grouped(rangeFollowUps.map((item) => item.channel)), salespeople: [...salespersonMap.values()].sort((left, right) => right.won_amount - left.won_amount || right.customer_count - left.customer_count),
  };
}

function customerListItem(customer: Customer, ownerName: string | null, sampleCount: number, opportunityCount: number): CustomerListItem {
  return { ...customer, owner_name: ownerName, sample_count: sampleCount, opportunity_count: opportunityCount };
}

function cleanSearch(value = "") {
  return value.trim().replace(/[,%(){}]/g, " ").replace(/\s+/g, " ").slice(0, 120);
}

export async function listCustomersPage(query: CustomerListQuery): Promise<CustomerListResult> {
  const page = Math.max(1, query.page || 1);
  const pageSize = Math.min(100, Math.max(10, query.pageSize || 20));
  const search = cleanSearch(query.search);
  const sort = query.sort ?? "updated_at";
  const order = query.order ?? "desc";

  if (!isSupabaseConfigured || !supabase) {
    let rows = loadDemoCustomers().map((customer) => customerListItem(
      customer,
      customer.assigned_to === "demo-user" ? "演示管理员" : null,
      loadDemoSamples().filter((sample) => sample.customer_id === customer.id).length,
      loadLocal(OPPORTUNITY_KEY, demoOpportunities).filter((opportunity) => opportunity.customer_id === customer.id).length,
    ));
    if (query.keyOnly) rows = rows.filter((customer) => customer.is_key_account);
    if (query.lifecycle) rows = rows.filter((customer) => customer.lifecycle_status === query.lifecycle);
    if (query.developmentStage && query.developmentStage !== "all") rows = rows.filter((customer) => customer.development_stage === query.developmentStage);
    if (query.sourceDetail && query.sourceDetail !== "all") rows = rows.filter((customer) => customer.source_detail === query.sourceDetail);
    if (query.createdFrom) rows = rows.filter((customer) => new Date(customer.created_at).getTime() >= new Date(query.createdFrom!).getTime());
    if (query.createdTo) rows = rows.filter((customer) => new Date(customer.created_at).getTime() < new Date(query.createdTo!).getTime());
    if (query.status && query.status !== "all") rows = rows.filter((customer) => customer.status === query.status);
    if (query.source && query.source !== "all") rows = rows.filter((customer) => customer.lead_source === query.source);
    if (search) {
      const needle = search.toLowerCase();
      rows = rows.filter((customer) => [customer.company_name, customer.contact_name, customer.email, customer.phone, customer.other_contact, customer.country, customer.inquiry_content, customer.product_interest, customer.source_detail, ...customer.tags].filter(Boolean).join(" ").toLowerCase().includes(needle));
    }
    rows.sort((left, right) => {
      const leftValue = String(left[sort] ?? "");
      const rightValue = String(right[sort] ?? "");
      return leftValue.localeCompare(rightValue, "zh-CN") * (order === "asc" ? 1 : -1);
    });
    const count = rows.length;
    const start = (page - 1) * pageSize;
    return { rows: rows.slice(start, start + pageSize), count, page, pageSize, pageCount: Math.max(1, Math.ceil(count / pageSize)) };
  }

  const columns = "id,customer_no,company_name,contact_name,country,website,email,phone,other_contact,inquiry_content,lead_source,status,grade,is_key_account,tags,assigned_to,team_id,last_follow_up_at,next_follow_up_at,lifecycle_status,development_stage,source_detail,product_interest,estimated_quantity,estimated_value,lead_score,lead_score_override,converted_at,converted_by,created_at,updated_at,assigned_profile:profiles!customers_assigned_to_fkey(full_name),samples(count),opportunities(count)";
  let request = supabase.from("customers").select(columns, { count: "exact" }).is("deleted_at", null);
  if (query.keyOnly) request = request.eq("is_key_account", true);
  if (query.lifecycle) request = request.eq("lifecycle_status", query.lifecycle);
  if (query.developmentStage && query.developmentStage !== "all") request = request.eq("development_stage", query.developmentStage);
  if (query.sourceDetail && query.sourceDetail !== "all") request = request.eq("source_detail", query.sourceDetail);
  if (query.createdFrom) request = request.gte("created_at", query.createdFrom);
  if (query.createdTo) request = request.lt("created_at", query.createdTo);
  if (query.status && query.status !== "all") request = request.eq("status", query.status);
  if (query.source && query.source !== "all") request = request.eq("lead_source", query.source);
  if (search) {
    const pattern = `%${search}%`;
    request = request.or(`company_name.ilike.${pattern},contact_name.ilike.${pattern},email.ilike.${pattern},phone.ilike.${pattern},other_contact.ilike.${pattern},country.ilike.${pattern},inquiry_content.ilike.${pattern},product_interest.ilike.${pattern},source_detail.ilike.${pattern},tags.cs.{${search}}`);
  }
  const start = (page - 1) * pageSize;
  const { data, error, count } = await request.order(sort, { ascending: order === "asc", nullsFirst: false }).range(start, start + pageSize - 1);
  if (error) throw dataError(error, "客户列表暂时无法加载，请稍后重试");
  const rows = (data ?? []).map((raw) => {
    const row = raw as unknown as Customer & { assigned_profile?: { full_name?: string | null } | Array<{ full_name?: string | null }> | null; samples?: Array<{ count?: number }>; opportunities?: Array<{ count?: number }> };
    const profile = Array.isArray(row.assigned_profile) ? row.assigned_profile[0] : row.assigned_profile;
    return customerListItem(row, profile?.full_name ?? null, Number(row.samples?.[0]?.count ?? 0), Number(row.opportunities?.[0]?.count ?? 0));
  });
  const total = count ?? 0;
  return { rows, count: total, page, pageSize, pageCount: Math.max(1, Math.ceil(total / pageSize)) };
}

function loadDemoProducts() {
  const stored = loadLocal(PRODUCT_KEY, demoProducts) as Product[];
  const ids = new Set(stored.map((product) => product.id));
  return [...stored, ...demoProducts.filter((product) => !ids.has(product.id))];
}

export async function listProductsPage(query: ProductListQuery): Promise<ProductListResult> {
  const page = Math.max(1, query.page || 1);
  const pageSize = Math.min(100, Math.max(10, query.pageSize || 20));
  const search = cleanSearch(query.search);
  const sort = query.sort ?? "updated_at";
  const order = query.order ?? "desc";

  if (!isSupabaseConfigured || !supabase) {
    let rows = loadDemoProducts();
    if (query.status && query.status !== "all") rows = rows.filter((product) => product.status === query.status);
    if (query.category && query.category !== "all") rows = rows.filter((product) => product.category === query.category);
    if (search) {
      const needle = search.toLowerCase();
      rows = rows.filter((product) => [product.product_no, product.name, product.english_name, product.category, product.specification, product.capacity, product.efficacy, product.formula_type, product.supplier_info].filter(Boolean).join(" ").toLowerCase().includes(needle));
    }
    rows.sort((left, right) => String(left[sort] ?? "").localeCompare(String(right[sort] ?? ""), "zh-CN", { numeric: true }) * (order === "asc" ? 1 : -1));
    const count = rows.length;
    const start = (page - 1) * pageSize;
    return { rows: rows.slice(start, start + pageSize), count, page, pageSize, pageCount: Math.max(1, Math.ceil(count / pageSize)) };
  }

  let request = supabase.from("products").select("*", { count: "exact" });
  if (query.status && query.status !== "all") request = request.eq("status", query.status);
  if (query.category && query.category !== "all") request = request.eq("category", query.category);
  if (search) {
    const pattern = `%${search}%`;
    request = request.or(`product_no.ilike.${pattern},name.ilike.${pattern},english_name.ilike.${pattern},category.ilike.${pattern},specification.ilike.${pattern},capacity.ilike.${pattern},efficacy.ilike.${pattern},formula_type.ilike.${pattern},supplier_info.ilike.${pattern}`);
  }
  const start = (page - 1) * pageSize;
  const { data, error, count } = await request.order(sort, { ascending: order === "asc", nullsFirst: false }).range(start, start + pageSize - 1);
  if (error) throw dataError(error, "产品档案暂时无法加载，请稍后重试", "PRODUCT_LIST_FAILED");
  const total = count ?? 0;
  return { rows: (data ?? []) as Product[], count: total, page, pageSize, pageCount: Math.max(1, Math.ceil(total / pageSize)) };
}

export async function getProduct(id: string): Promise<Product | null> {
  if (!isSupabaseConfigured || !supabase) return loadDemoProducts().find((product) => product.id === id) ?? null;
  const { data, error } = await supabase.from("products").select("*").eq("id", id).maybeSingle();
  if (error) throw dataError(error, "产品资料暂时无法加载，请稍后重试", "PRODUCT_LOAD_FAILED");
  return data as Product | null;
}

export async function listProductVersions(productId: string): Promise<ProductVersion[]> {
  if (!isSupabaseConfigured || !supabase) {
    const product = await getProduct(productId);
    return product ? [{ id: `demo-version-${product.id}`, product_id: product.id, version_no: 1, snapshot: product, changed_by: "demo-user", changed_by_name: "演示管理员", created_at: product.updated_at }] : [];
  }
  const { data, error } = await supabase.from("product_versions").select("id,product_id,version_no,snapshot,changed_by,created_at,changed_by_profile:profiles!product_versions_changed_by_fkey(full_name)").eq("product_id", productId).order("version_no", { ascending: false });
  if (error) throw dataError(error, "产品版本历史暂时无法加载，请稍后重试", "PRODUCT_VERSION_FAILED");
  return (data ?? []).map((row) => ({ ...row, changed_by_name: (row.changed_by_profile as { full_name?: string } | null)?.full_name || "系统" })) as unknown as ProductVersion[];
}

export async function saveProduct(input: ProductInput, id?: string): Promise<Product> {
  const values = cleanInput(input) as Partial<Product>;
  const now = new Date().toISOString();
  if (!isSupabaseConfigured || !supabase) {
    const rows = loadDemoProducts();
    if (id) {
      const index = rows.findIndex((product) => product.id === id);
      if (index < 0) throw new Error("产品档案不存在");
      rows[index] = { ...rows[index], ...values, updated_at: now };
      saveLocal(PRODUCT_KEY, rows);
      return rows[index];
    }
    const next = { id: crypto.randomUUID(), english_name: null, specification: null, capacity: null, unit: "件", default_moq: null, sample_cycle_days: null, default_packaging: null, efficacy: null, formula_type: null, supplier_info: null, status: "active", formula_requirements: null, efficacy_requirements: null, packaging_details: null, pricing_details: null, engineering_notes: null, image_urls: [], created_at: now, updated_at: now, ...values } as Product;
    rows.unshift(next);
    saveLocal(PRODUCT_KEY, rows);
    return next;
  }
  const { data: authData } = await supabase.auth.getUser();
  const payload = { ...values, updated_by: authData.user?.id, ...(id ? {} : { created_by: authData.user?.id }) };
  const request = id ? supabase.from("products").update(payload).eq("id", id) : supabase.from("products").insert(payload);
  const { data, error } = await request.select("*").single();
  if (error) throw dataError(error, "产品档案保存失败，请检查编号是否重复", "PRODUCT_SAVE_FAILED");
  return data as Product;
}

export async function setProductStatus(id: string, status: Product["status"]) {
  const current = await getProduct(id);
  if (!current) throw new Error("产品档案不存在");
  return saveProduct({ product_no: current.product_no, name: current.name, category: current.category, status }, id);
}

export async function listAssignableProfiles(): Promise<Array<Pick<Profile, "id" | "full_name" | "role" | "is_active">>> {
  if (!isSupabaseConfigured || !supabase) return [
    { id: "demo-user", full_name: "演示管理员", role: "super_admin", is_active: true },
    { id: "demo-sales", full_name: "示例业务员", role: "salesperson", is_active: true },
  ];
  const { data, error } = await supabase.from("profiles").select("id,full_name,role,is_active").eq("is_active", true).order("full_name");
  if (error) throw dataError(error, "负责人列表暂时无法加载，请稍后重试");
  return (data ?? []) as Array<Pick<Profile, "id" | "full_name" | "role" | "is_active">>;
}

export async function bulkUpdateCustomers(ids: string[], input: CustomerInput): Promise<number> {
  const uniqueIds = [...new Set(ids)].slice(0, 100);
  if (!uniqueIds.length) return 0;
  const values = cleanInput(input);
  if (!isSupabaseConfigured || !supabase) {
    const current = loadDemoCustomers();
    const now = new Date().toISOString();
    let updated = 0;
    const rows = current.map((customer) => {
      if (!uniqueIds.includes(customer.id)) return customer;
      updated += 1;
      const next = { ...customer, ...values, updated_at: now } as Customer;
      appendDemoCustomerAudit(customer, next, now);
      return next;
    });
    saveLocal(STORAGE_KEY, rows);
    return updated;
  }
  const { data: authData } = await supabase.auth.getUser();
  const { data, error } = await supabase.from("customers").update({ ...values, updated_by: authData.user?.id }).in("id", uniqueIds).select("id");
  if (error) throw dataError(error, "批量更新暂时无法完成，请稍后重试");
  return data?.length ?? 0;
}

export async function getCustomer(id: string): Promise<Customer | null> {
  if (!isSupabaseConfigured || !supabase) {
    return loadDemoCustomers().find((customer) => customer.id === id) ?? null;
  }
  const { data, error } = await supabase.from("customers").select("*").eq("id", id).maybeSingle();
  if (error) throw dataError(error, "客户详情暂时无法加载，请稍后重试");
  return data as Customer | null;
}

export async function findPotentialCustomerDuplicates(input: { email?: string; phone?: string; excludeId?: string }): Promise<Array<Pick<Customer, "id" | "company_name" | "contact_name" | "email" | "phone" | "lifecycle_status">>> {
  const email = input.email?.trim().toLowerCase() || "";
  const phone = input.phone?.replace(/\D/g, "") || "";
  if (!email && !phone) return [];
  if (!isSupabaseConfigured || !supabase) {
    return loadDemoCustomers().filter((customer) => customer.id !== input.excludeId && ((email && customer.email?.trim().toLowerCase() === email) || (phone && customer.phone?.replace(/\D/g, "") === phone))).map(({ id, company_name, contact_name, lifecycle_status, email: rowEmail, phone: rowPhone }) => ({ id, company_name, contact_name, lifecycle_status, email: rowEmail, phone: rowPhone }));
  }
  const filters = [email ? `email_normalized.eq.${email.replace(/[,()]/g, "")}` : "", phone ? `phone_normalized.eq.${phone}` : ""].filter(Boolean).join(",");
  let request = supabase.from("customers").select("id,company_name,contact_name,email,phone,lifecycle_status").is("deleted_at", null).or(filters).limit(5);
  if (input.excludeId) request = request.neq("id", input.excludeId);
  const { data, error } = await request;
  if (error) throw dataError(error, "客户查重暂时无法完成，请稍后重试");
  return (data ?? []) as Array<Pick<Customer, "id" | "company_name" | "contact_name" | "email" | "phone" | "lifecycle_status">>;
}

export async function convertLeadToCustomer(id: string): Promise<Customer> {
  if (!isSupabaseConfigured || !supabase) {
    const before = await getCustomer(id);
    if (!before) throw new ApiError({ code: "LEAD_NOT_FOUND", userMessage: "潜客不存在或已被处理", status: 404, retryable: false });
    if (before.lifecycle_status !== "prospect") return before;
    const converted = await saveCustomer({ lifecycle_status: "customer", converted_at: new Date().toISOString(), converted_by: "demo-user" }, id);
    const audits = loadLocal(AUDIT_KEY, demoAuditLogs);
    audits.unshift({ id: crypto.randomUUID(), actor_id: "demo-user", actor_name: "演示管理员", action: "lead.convert", entity_type: "customer", entity_id: id, customer_id: id, details: { summary: `潜客已转为正式客户：${converted.company_name || converted.contact_name || converted.customer_no}` }, created_at: converted.updated_at });
    saveLocal(AUDIT_KEY, audits);
    return converted;
  }
  const { data, error } = await supabase.rpc("convert_lead_to_customer", { p_customer_id: id });
  if (error) throw dataError(error, "潜客暂时无法转为正式客户", "LEAD_CONVERT_FAILED");
  return data as Customer;
}

export async function mergeCustomers(sourceId: string, targetId: string): Promise<Customer> {
  if (sourceId === targetId) throw new ApiError({ code: "CUSTOMER_MERGE_SAME_RECORD", userMessage: "不能合并同一条客户记录", status: 400, retryable: false });
  if (!isSupabaseConfigured || !supabase) {
    const customers = loadDemoCustomers();
    const source = customers.find((customer) => customer.id === sourceId);
    const target = customers.find((customer) => customer.id === targetId);
    if (!source || !target) throw new ApiError({ code: "CUSTOMER_MERGE_NOT_FOUND", userMessage: "待合并客户不存在或已被处理", status: 404, retryable: false });
    const now = new Date().toISOString();
    const merged: Customer = { ...target, company_name: target.company_name || source.company_name, contact_name: target.contact_name || source.contact_name, country: target.country || source.country, website: target.website || source.website, email: target.email || source.email, phone: target.phone || source.phone, other_contact: target.other_contact || source.other_contact, inquiry_content: target.inquiry_content || source.inquiry_content, product_interest: target.product_interest || source.product_interest, estimated_quantity: target.estimated_quantity || source.estimated_quantity, estimated_value: target.estimated_value || source.estimated_value, tags: [...new Set([...target.tags, ...source.tags])], is_key_account: target.is_key_account || source.is_key_account, lifecycle_status: target.lifecycle_status === "customer" || source.lifecycle_status === "customer" ? "customer" : "prospect", lead_score: Math.max(target.lead_score, source.lead_score), updated_at: now };
    saveLocal(STORAGE_KEY, customers.filter((customer) => customer.id !== sourceId).map((customer) => customer.id === targetId ? merged : customer));
    saveLocal(DELETED_CUSTOMER_KEY, [...new Set([...loadLocal<string[]>(DELETED_CUSTOMER_KEY, []), sourceId])]);
    saveLocal(FOLLOW_UP_KEY, loadLocal(FOLLOW_UP_KEY, demoFollowUps).map((item) => item.customer_id === sourceId ? { ...item, customer_id: targetId } : item));
    saveLocal(REMINDER_KEY, loadLocal(REMINDER_KEY, demoReminders).map((item) => item.customer_id === sourceId ? { ...item, customer_id: targetId } : item));
    saveLocal(SAMPLE_KEY, loadDemoSamples().map((item) => item.customer_id === sourceId ? { ...item, customer_id: targetId } : item));
    saveLocal(OPPORTUNITY_KEY, loadLocal(OPPORTUNITY_KEY, demoOpportunities).map((item) => item.customer_id === sourceId ? { ...item, customer_id: targetId } : item));
    saveLocal(QUOTE_KEY, loadLocal(QUOTE_KEY, demoQuotes).map((item) => item.customer_id === sourceId ? { ...item, customer_id: targetId } : item));
    saveLocal(ATTACHMENT_KEY, loadLocal<Attachment[]>(ATTACHMENT_KEY, []).map((item) => item.customer_id === sourceId ? { ...item, customer_id: targetId } : item));
    const audits = loadLocal(AUDIT_KEY, demoAuditLogs).map((item) => item.customer_id === sourceId ? { ...item, customer_id: targetId } : item);
    audits.unshift({ id: crypto.randomUUID(), actor_id: "demo-user", actor_name: "演示管理员", action: "customer.merge", entity_type: "customer", entity_id: targetId, customer_id: targetId, details: { summary: `合并重复客户：${source.company_name || source.contact_name || source.customer_no} → ${target.company_name || target.contact_name || target.customer_no}`, source_customer_id: sourceId, target_customer_id: targetId }, created_at: now });
    saveLocal(AUDIT_KEY, audits);
    return merged;
  }
  const { data, error } = await supabase.rpc("merge_customers", { p_source_id: sourceId, p_target_id: targetId });
  if (error) throw dataError(error, "重复客户合并失败，请检查权限后重试", "CUSTOMER_MERGE_FAILED");
  return data as Customer;
}

export async function saveCustomer(input: CustomerInput, id?: string): Promise<Customer> {
  const values = cleanInput(input);
  if (!isSupabaseConfigured || !supabase) {
    const current = loadDemoCustomers();
    const now = new Date().toISOString();
    if (id) {
      const index = current.findIndex((customer) => customer.id === id);
      if (index < 0) throw new Error("客户不存在");
      const before = current[index];
      current[index] = { ...before, ...values, updated_at: now } as Customer;
      appendDemoCustomerAudit(before, current[index], now);
      saveLocal(STORAGE_KEY, current);
      return current[index];
    }
    const next: Customer = {
      id: crypto.randomUUID(),
      customer_no: Math.max(10000, ...current.map((customer) => customer.customer_no)) + 1,
      company_name: null,
      contact_name: null,
      country: null,
      website: null,
      email: null,
      phone: null,
      other_contact: null,
      inquiry_content: null,
      lead_source: "manual",
      status: "new",
      grade: null,
      is_key_account: false,
      tags: [],
      assigned_to: "demo-user",
      team_id: "demo-team",
      last_follow_up_at: null,
      next_follow_up_at: null,
      lifecycle_status: "customer",
      development_stage: "new_lead",
      source_detail: null,
      product_interest: null,
      estimated_quantity: null,
      estimated_value: null,
      lead_score: 0,
      lead_score_override: false,
      converted_at: null,
      converted_by: null,
      created_at: now,
      updated_at: now,
      ...values,
    } as Customer;
    current.unshift(next);
    saveLocal(STORAGE_KEY, current);
    return next;
  }

  const { data: authData } = await supabase.auth.getUser();
  const payload = {
    ...values,
    updated_by: authData.user?.id,
    ...(id ? {} : { created_by: authData.user?.id, assigned_to: authData.user?.id }),
  };
  const request = id
    ? supabase.from("customers").update(payload).eq("id", id)
    : supabase.from("customers").insert(payload);
  const { data, error } = await request.select("*").single();
  if (error) throw dataError(error, "客户资料暂时无法保存，请稍后重试");
  return data as Customer;
}

export async function listFollowUps(customerId?: string): Promise<FollowUp[]> {
  if (!isSupabaseConfigured || !supabase) {
    const rows = loadLocal(FOLLOW_UP_KEY, demoFollowUps);
    return customerId ? rows.filter((row) => row.customer_id === customerId) : rows;
  }
  let request = supabase.from("follow_ups").select("*,author:profiles!follow_ups_author_id_fkey(full_name)").is("deleted_at", null).order("occurred_at", { ascending: false }).limit(500);
  if (customerId) request = request.eq("customer_id", customerId);
  const { data, error } = await request;
  if (error) throw dataError(error, "跟进记录暂时无法加载，请稍后重试");
  return (data ?? []).map((raw) => {
    const row = raw as unknown as FollowUp & { author?: { full_name?: string | null } | null };
    return { ...row, author_name: row.author?.full_name || "业务员" };
  });
}

export async function addFollowUp(input: Pick<FollowUp, "customer_id" | "channel" | "content" | "next_follow_up_at">): Promise<FollowUp> {
  if (!isSupabaseConfigured || !supabase) {
    const rows = loadLocal(FOLLOW_UP_KEY, demoFollowUps);
    const next: FollowUp = {
      id: crypto.randomUUID(),
      author_id: "demo-user",
      author_name: "演示管理员",
      subject: null,
      occurred_at: new Date().toISOString(),
      ...input,
    };
    rows.unshift(next);
    saveLocal(FOLLOW_UP_KEY, rows);
    const customers = loadDemoCustomers();
    const index = customers.findIndex((customer) => customer.id === input.customer_id);
    if (index >= 0) {
      customers[index] = {
        ...customers[index],
        last_follow_up_at: next.occurred_at,
        next_follow_up_at: input.next_follow_up_at ?? customers[index].next_follow_up_at,
        updated_at: next.occurred_at,
      };
      saveLocal(STORAGE_KEY, customers);
    }
    if (input.next_follow_up_at) {
      const reminders = loadLocal(REMINDER_KEY, demoReminders);
      reminders.unshift({ id: crypto.randomUUID(), customer_id: input.customer_id, owner_id: "demo-user", owner_name: "演示管理员", title: input.content.trim().slice(0, 80) || "客户下次跟进", due_at: input.next_follow_up_at, completed_at: null, due_status: reminderDueStatus(input.next_follow_up_at, null), created_at: next.occurred_at, updated_at: next.occurred_at, customer: customers[index] });
      saveLocal(REMINDER_KEY, reminders);
    }
    return next;
  }
  const { data: authData } = await supabase.auth.getUser();
  const { data, error } = await supabase.from("follow_ups").insert({ ...input, author_id: authData.user?.id }).select("*").single();
  if (error) throw dataError(error, "跟进记录暂时无法保存，请稍后重试");
  return data as FollowUp;
}

function reminderDueStatus(dueAt: string, completedAt: string | null): Reminder["due_status"] {
  if (completedAt) return "completed";
  const due = new Date(dueAt).getTime();
  const now = Date.now();
  if (due < now) return "overdue";
  if (due < now + 3 * 86_400_000) return "due_soon";
  return "upcoming";
}

export async function listReminders(customerId?: string): Promise<Reminder[]> {
  if (!isSupabaseConfigured || !supabase) {
    const rows = loadLocal(REMINDER_KEY, demoReminders).map((row) => ({ ...row, due_status: reminderDueStatus(row.due_at, row.completed_at) }));
    return customerId ? rows.filter((row) => row.customer_id === customerId) : rows;
  }
  let request = supabase.from("reminder_status").select("*,customer:customers(company_name, contact_name),owner:profiles!reminders_owner_id_fkey(full_name)").order("due_at").limit(500);
  if (customerId) request = request.eq("customer_id", customerId);
  const { data, error } = await request;
  if (error) throw dataError(error, "提醒数据暂时无法加载，请稍后重试");
  return (data ?? []).map((raw) => {
    const row = raw as Record<string, unknown> & { owner?: { full_name?: string | null } | null };
    return { ...row, customer: row.customer, owner_name: row.owner?.full_name || "业务员" } as Reminder;
  });
}

export async function updateReminder(id: string, input: { title?: string; due_at?: string; completed_at?: string | null }): Promise<Reminder> {
  const values = Object.fromEntries(Object.entries(input).filter(([, value]) => value !== undefined));
  if (!isSupabaseConfigured || !supabase) {
    const rows = loadLocal(REMINDER_KEY, demoReminders);
    const index = rows.findIndex((row) => row.id === id);
    if (index < 0) throw new Error("待办不存在或已被删除");
    const updatedAt = new Date().toISOString();
    const dueAt = typeof values.due_at === "string" ? values.due_at : rows[index].due_at;
    const completedAt = Object.prototype.hasOwnProperty.call(values, "completed_at") ? (values.completed_at as string | null) : rows[index].completed_at;
    rows[index] = { ...rows[index], ...values, updated_at: updatedAt, due_status: reminderDueStatus(dueAt, completedAt) };
    saveLocal(REMINDER_KEY, rows);
    return rows[index];
  }
  const { data, error } = await supabase.from("reminders").update(values).eq("id", id).select("*,customer:customers(company_name, contact_name),owner:profiles!reminders_owner_id_fkey(full_name)").single();
  if (error) throw dataError(error, "待办暂时无法更新，请稍后重试");
  const row = data as unknown as Reminder & { owner?: { full_name?: string | null } | null };
  return { ...row, owner_name: row.owner?.full_name || "业务员", due_status: reminderDueStatus(row.due_at, row.completed_at) };
}

export async function completeReminderWithFollowUp(input: { reminderId: string; channel: FollowUp["channel"]; content: string; next_follow_up_at: string | null }): Promise<void> {
  if (!isSupabaseConfigured || !supabase) {
    const reminder = loadLocal(REMINDER_KEY, demoReminders).find((row) => row.id === input.reminderId);
    if (!reminder) throw new Error("待办不存在或已被删除");
    await addFollowUp({ customer_id: reminder.customer_id, channel: input.channel, content: input.content, next_follow_up_at: input.next_follow_up_at });
    await updateReminder(reminder.id, { completed_at: new Date().toISOString() });
    return;
  }
  const { error } = await supabase.rpc("complete_follow_up_task", { p_reminder_id: input.reminderId, p_channel: input.channel, p_content: input.content, p_next_follow_up_at: input.next_follow_up_at });
  if (error) throw dataError(error, "跟进暂时无法完成，请稍后重试");
}

export async function listSamples(customerId?: string): Promise<Sample[]> {
  if (!isSupabaseConfigured || !supabase) {
    const rows = loadDemoSamples();
    return customerId ? rows.filter((row) => row.customer_id === customerId) : rows;
  }
  let request = supabase.from("samples").select("*,customer:customers(company_name, contact_name),owner:profiles!samples_owner_id_fkey(full_name)").is("deleted_at", null).order("updated_at", { ascending: false }).limit(500);
  if (customerId) request = request.eq("customer_id", customerId);
  const { data, error } = await request;
  if (error) throw dataError(error, "样品数据暂时无法加载，请稍后重试");
  return (data ?? []).map((raw) => {
    const row = raw as Record<string, unknown> & { owner?: { full_name?: string | null } | null };
    return { ...row, customer: row.customer, owner_name: row.owner?.full_name || "业务员" } as Sample;
  });
}

export async function listSamplesPage(query: SampleListQuery): Promise<SampleListResult> {
  const page = Math.max(1, query.page || 1);
  const pageSize = Math.min(100, Math.max(10, query.pageSize || 20));
  const search = cleanSearch(query.search);
  const sort = query.sort ?? "updated_at";
  const order = query.order ?? "desc";

  if (!isSupabaseConfigured || !supabase) {
    let rows = loadDemoSamples();
    if (query.status && query.status !== "all") rows = rows.filter((sample) => sample.status === query.status);
    if (search) {
      const needle = search.toLowerCase();
      rows = rows.filter((sample) => [sample.product_name, sample.customer?.company_name, sample.customer?.contact_name, sample.logistics_company, sample.tracking_no].filter(Boolean).join(" ").toLowerCase().includes(needle));
    }
    rows = [...rows].sort((left, right) => {
      const leftValue = String(left[sort] ?? (sort === "next_follow_up_at" ? "9999" : ""));
      const rightValue = String(right[sort] ?? (sort === "next_follow_up_at" ? "9999" : ""));
      return leftValue.localeCompare(rightValue, "zh-CN") * (order === "asc" ? 1 : -1);
    });
    const count = rows.length;
    const start = (page - 1) * pageSize;
    return { rows: rows.slice(start, start + pageSize), count, page, pageSize, pageCount: Math.max(1, Math.ceil(count / pageSize)) };
  }

  const columns = "id,sample_no,customer_id,product_id,product_name,version_no,parent_sample_id,status,priority,current_department,current_assignee,target_market,expected_ship_date,target_order_quantity,target_unit_price,submitted_at,accepted_at,started_at,completed_at,due_at,shipping_fee,sample_fee,currency,payment_method,is_paid,is_shipped,logistics_company,tracking_no,shipped_at,delivered_at,customer_feedback,test_result,next_step,next_follow_up_at,notes,owner_id,created_at,updated_at,customer:customers(company_name,contact_name),owner:profiles!samples_owner_id_fkey(full_name)";
  let request = supabase.from("samples").select(columns, { count: "exact" }).is("deleted_at", null);
  if (query.status && query.status !== "all") request = request.eq("status", query.status);
  if (search) {
    const matchingCustomers = await listCustomersPage({ page: 1, pageSize: 100, search, sort: "updated_at", order: "desc" });
      const clauses = [`sample_no.ilike.%${search}%`, `product_name.ilike.%${search}%`, `logistics_company.ilike.%${search}%`, `tracking_no.ilike.%${search}%`];
    if (matchingCustomers.rows.length) clauses.push(`customer_id.in.(${matchingCustomers.rows.map((customer) => customer.id).join(",")})`);
    request = request.or(clauses.join(","));
  }
  const start = (page - 1) * pageSize;
  const { data, error, count } = await request.order(sort, { ascending: order === "asc", nullsFirst: false }).range(start, start + pageSize - 1);
  if (error) throw dataError(error, "样品列表暂时无法加载，请稍后重试");
  const rows = (data ?? []).map((raw) => {
    const row = raw as unknown as Sample & { owner?: { full_name?: string | null } | null };
    return { ...row, owner_name: row.owner?.full_name || "业务员" };
  });
  const total = count ?? 0;
  return { rows, count: total, page, pageSize, pageCount: Math.max(1, Math.ceil(total / pageSize)) };
}

export async function createSample(input: SampleInput): Promise<Sample> {
  if (!isSupabaseConfigured || !supabase) {
    const rows = loadDemoSamples();
    const customer = (await listCustomers()).find((item) => item.id === input.customer_id);
    const createdAt = new Date().toISOString();
    const next = { id: crypto.randomUUID(), sample_no: `YKY-S-${createdAt.slice(0, 10).replaceAll("-", "")}-${String(rows.length + 1).padStart(3, "0")}`, product_id: input.product_id || null, version_no: 1, parent_sample_id: null, status: "draft", priority: input.priority || "normal", current_department: "sales", current_assignee: "demo-user", target_market: input.target_market || customer?.country || null, expected_ship_date: input.expected_ship_date || null, target_order_quantity: input.target_order_quantity || null, target_unit_price: input.target_unit_price || null, submitted_at: null, accepted_at: null, started_at: null, completed_at: null, due_at: null, owner_id: "demo-user", owner_name: "演示管理员", created_at: createdAt, updated_at: createdAt, ...input, customer: customer ? { company_name: customer.company_name, contact_name: customer.contact_name } : undefined } as Sample;
    rows.unshift(next); saveLocal(SAMPLE_KEY, rows); return next;
  }
  const { data: authData } = await supabase.auth.getUser();
  const { data, error } = await supabase.from("samples").insert({ ...input, status: "draft", owner_id: authData.user?.id, current_assignee: authData.user?.id, current_department: "sales", sample_no: `YKY-S-${Date.now().toString(36).toUpperCase()}`, created_by: authData.user?.id }).select("*, customers(company_name, contact_name)").single();
  if (error) throw dataError(error, "样品记录暂时无法保存，请稍后重试");
  return { ...data, customer: data.customers } as Sample;
}

export async function updateSample(id: string, input: Partial<SampleInput>): Promise<Sample> {
  if ("status" in input) throw new ApiError({ code: "SAMPLE_STATUS_TRANSITION_REQUIRED", userMessage: "样品状态必须通过流程操作变更", technicalMessage: "Direct sample status update rejected", status: 400, retryable: false });
  if (!isSupabaseConfigured || !supabase) {
    const rows = loadDemoSamples();
    const index = rows.findIndex((row) => row.id === id);
    if (index < 0) throw new Error("样品记录不存在");
    const before = rows[index];
    rows[index] = { ...before, ...input, updated_at: new Date().toISOString() };
    saveLocal(SAMPLE_KEY, rows);
    const audits = loadLocal(AUDIT_KEY, demoAuditLogs);
    audits.unshift({ id: crypto.randomUUID(), actor_id: "demo-user", actor_name: "演示管理员", action: before.status === rows[index].status ? "sample.update" : "sample.status_change", entity_type: "samples", entity_id: id, customer_id: rows[index].customer_id, details: { from: before.status, to: rows[index].status, summary: before.status === rows[index].status ? `更新样品：${rows[index].product_name}` : `样品状态从 ${before.status} 调整为 ${rows[index].status}` }, created_at: rows[index].updated_at });
    saveLocal(AUDIT_KEY, audits);
    return rows[index];
  }
  const { data: authData } = await supabase.auth.getUser();
  const { data, error } = await supabase.from("samples").update({ ...input, updated_by: authData.user?.id }).eq("id", id).select("*,customer:customers(company_name, contact_name),owner:profiles!samples_owner_id_fkey(full_name)").single();
  if (error) throw dataError(error, "样品记录暂时无法更新，请稍后重试");
  const row = data as unknown as Sample & { owner?: { full_name?: string | null } | null };
  return { ...row, owner_name: row.owner?.full_name || "业务员" };
}

export async function getSample(id: string): Promise<Sample | null> {
  if (!isSupabaseConfigured || !supabase) return loadDemoSamples().find((sample) => sample.id === id) ?? null;
  const { data, error } = await supabase.from("samples").select("*,customer:customers(company_name,contact_name),owner:profiles!samples_owner_id_fkey(full_name)").eq("id", id).is("deleted_at", null).maybeSingle();
  if (error) throw dataError(error, "样品详情暂时无法加载，请稍后重试", "SAMPLE_LOAD_FAILED");
  if (!data) return null;
  const row = data as unknown as Sample & { owner?: { full_name?: string | null } | null };
  return { ...row, owner_name: row.owner?.full_name || "业务员" };
}

function departmentForSampleStatus(status: SampleWorkflowStatus): Sample["current_department"] {
  if (["submitted_to_engineering", "engineering_review", "business_resubmitted"].includes(status)) return "engineering";
  if (["submitted_to_factory", "factory_review", "factory_needs_change", "factory_approved", "material_pending", "packaging_pending", "production_queued", "scheduled", "in_production", "production_exception", "production_completed"].includes(status)) return "factory";
  if (["qc_pending", "qc_in_progress", "qc_failed", "qc_conditional_pass", "qc_passed"].includes(status)) return "quality";
  if (["ready_to_ship", "shipped", "in_transit", "delivered"].includes(status)) return "logistics";
  return "sales";
}

export async function transitionSampleWorkflow(id: string, toStatus: SampleWorkflowStatus, comment?: string): Promise<Sample> {
  if (!isSupabaseConfigured || !supabase) {
    const rows = loadDemoSamples();
    const index = rows.findIndex((sample) => sample.id === id);
    if (index < 0) throw new Error("样品申请不存在");
    const before = rows[index];
    if (!getAllowedTransitions(before.status).includes(toStatus)) throw new ApiError({ code: "SAMPLE_ILLEGAL_TRANSITION", userMessage: "当前状态不能执行此流程操作", technicalMessage: `${before.status} -> ${toStatus}`, status: 409, retryable: false });
    const now = new Date().toISOString();
    rows[index] = { ...before, status: toStatus, current_department: departmentForSampleStatus(toStatus), submitted_at: toStatus === "submitted_to_engineering" ? before.submitted_at || now : before.submitted_at, accepted_at: ["engineering_review", "factory_review"].includes(toStatus) ? before.accepted_at || now : before.accepted_at, started_at: toStatus === "in_production" ? before.started_at || now : before.started_at, completed_at: ["production_completed", "closed"].includes(toStatus) ? now : before.completed_at, due_at: toStatus === "submitted_to_engineering" ? new Date(Date.now() + 24 * 3_600_000).toISOString() : toStatus === "submitted_to_factory" ? new Date(Date.now() + 48 * 3_600_000).toISOString() : before.due_at, updated_at: now };
    saveLocal(SAMPLE_KEY, rows);
    const events = loadLocal<SampleWorkflowEvent[]>(SAMPLE_EVENT_KEY, []);
    events.unshift({ id: crypto.randomUUID(), sample_id: id, actor_id: "demo-user", actor_name: "演示管理员", department: rows[index].current_department, action: "status_transition", old_status: before.status, new_status: toStatus, comment: comment?.trim() || null, created_at: now });
    saveLocal(SAMPLE_EVENT_KEY, events);
    return rows[index];
  }
  const { data, error } = await supabase.rpc("transition_sample_workflow", { p_sample_id: id, p_to_status: toStatus, p_comment: comment?.trim() || null });
  if (error) throw dataError(error, error.message?.includes("SAMPLE_ILLEGAL_TRANSITION") ? "当前状态不能执行此流程操作" : "样品流程操作失败，请稍后重试", "SAMPLE_TRANSITION_FAILED");
  return data as Sample;
}

export async function listSampleWorkflowEvents(sampleId: string): Promise<SampleWorkflowEvent[]> {
  if (!isSupabaseConfigured || !supabase) {
    const sample = await getSample(sampleId);
    const saved = loadLocal<SampleWorkflowEvent[]>(SAMPLE_EVENT_KEY, []).filter((event) => event.sample_id === sampleId);
    return [...saved, ...(sample ? [{ id: `created-${sample.id}`, sample_id: sample.id, actor_id: sample.owner_id, actor_name: sample.owner_name || "业务员", department: "sales", action: "created", old_status: null, new_status: "draft" as const, comment: "创建样品申请单", created_at: sample.created_at }] : [])].sort((a, b) => b.created_at.localeCompare(a.created_at));
  }
  const { data, error } = await supabase.from("sample_workflow_events").select("id,sample_id,actor_id,department,action,old_status,new_status,comment,created_at,actor:profiles!sample_workflow_events_actor_id_fkey(full_name)").eq("sample_id", sampleId).order("created_at", { ascending: false });
  if (error) throw dataError(error, "样品审批历史暂时无法加载", "SAMPLE_EVENTS_FAILED");
  return (data ?? []).map((row) => ({ ...row, actor_name: (row.actor as { full_name?: string } | null)?.full_name || "系统" })) as unknown as SampleWorkflowEvent[];
}

export async function createSampleVersion(sampleId: string, comment?: string): Promise<Sample> {
  if (!isSupabaseConfigured || !supabase) {
    const source = await getSample(sampleId);
    if (!source) throw new Error("样品申请不存在");
    const rows = loadDemoSamples();
    const version = Math.max(...rows.filter((sample) => (sample.parent_sample_id || sample.id) === (source.parent_sample_id || source.id)).map((sample) => sample.version_no), source.version_no) + 1;
    const now = new Date().toISOString();
    const next = { ...source, id: crypto.randomUUID(), sample_no: source.sample_no.replace(/-V\d+$/, "") + `-V${version}`, version_no: version, parent_sample_id: source.parent_sample_id || source.id, status: "draft" as const, current_department: "sales" as const, current_assignee: source.owner_id, is_shipped: false, tracking_no: null, shipped_at: null, delivered_at: null, customer_feedback: null, test_result: null, submitted_at: null, accepted_at: null, started_at: null, completed_at: null, due_at: null, notes: comment?.trim() || `基于 V${source.version_no} 创建`, created_at: now, updated_at: now };
    rows.unshift(next); saveLocal(SAMPLE_KEY, rows); return next;
  }
  const { data, error } = await supabase.rpc("create_sample_version", { p_sample_id: sampleId, p_comment: comment?.trim() || null });
  if (error) throw dataError(error, "新样品版本创建失败，请稍后重试", "SAMPLE_VERSION_FAILED");
  return data as Sample;
}

export async function listSampleDiscussions(sampleId: string): Promise<SampleDiscussion[]> {
  if (!isSupabaseConfigured || !supabase) return loadLocal<SampleDiscussion[]>(SAMPLE_DISCUSSION_KEY, []).filter((item) => item.sample_id === sampleId).sort((a, b) => a.created_at.localeCompare(b.created_at));
  const { data, error } = await supabase.from("sample_discussions").select("id,sample_id,author_id,department,content,parent_id,created_at,author:profiles!sample_discussions_author_id_fkey(full_name)").eq("sample_id", sampleId).order("created_at");
  if (error) throw dataError(error, "内部沟通暂时无法加载", "SAMPLE_DISCUSSION_FAILED");
  return (data ?? []).map((row) => ({ ...row, author_name: (row.author as { full_name?: string } | null)?.full_name || "成员" })) as unknown as SampleDiscussion[];
}

export async function addSampleDiscussion(sampleId: string, content: string, department: string): Promise<SampleDiscussion> {
  if (!isSupabaseConfigured || !supabase) {
    const rows = loadLocal<SampleDiscussion[]>(SAMPLE_DISCUSSION_KEY, []);
    const next = { id: crypto.randomUUID(), sample_id: sampleId, author_id: "demo-user", author_name: "演示管理员", department, content: content.trim(), parent_id: null, created_at: new Date().toISOString() };
    rows.push(next); saveLocal(SAMPLE_DISCUSSION_KEY, rows); return next;
  }
  const { data, error } = await supabase.rpc("add_sample_discussion", { p_sample_id: sampleId, p_content: content.trim() });
  if (error) throw dataError(error, "内部沟通消息发送失败", "SAMPLE_DISCUSSION_SEND_FAILED");
  return data as SampleDiscussion;
}

export async function deleteSample(id: string): Promise<void> {
  if (!isSupabaseConfigured || !supabase) {
    const rows = loadDemoSamples();
    const target = rows.find((row) => row.id === id);
    if (!target) throw new Error("样品记录不存在");
    saveLocal(SAMPLE_KEY, rows.filter((row) => row.id !== id));
    return;
  }
  const { data: authData } = await supabase.auth.getUser();
  const { error } = await supabase.from("samples").update({ deleted_at: new Date().toISOString(), updated_by: authData.user?.id }).eq("id", id);
  if (error) throw dataError(error, "样品记录暂时无法删除，请稍后重试");
}

export async function listOpportunities(customerId?: string): Promise<Opportunity[]> {
  if (!isSupabaseConfigured || !supabase) {
    const rows = loadLocal(OPPORTUNITY_KEY, demoOpportunities);
    return customerId ? rows.filter((row) => row.customer_id === customerId) : rows;
  }
  let request = supabase.from("opportunities").select("*,customer:customers(company_name, contact_name),owner:profiles!opportunities_owner_id_fkey(full_name)").is("deleted_at", null).order("updated_at", { ascending: false }).limit(500);
  if (customerId) request = request.eq("customer_id", customerId);
  const { data, error } = await request;
  if (error) throw dataError(error, "商机数据暂时无法加载，请稍后重试");
  return (data ?? []).map((raw) => {
    const row = raw as Record<string, unknown> & { owner?: { full_name?: string | null } | null };
    return { ...row, customer: row.customer, owner_name: row.owner?.full_name || "业务员" } as Opportunity;
  });
}

export async function createOpportunity(input: OpportunityInput): Promise<Opportunity> {
  if (!isSupabaseConfigured || !supabase) {
    const rows = loadLocal(OPPORTUNITY_KEY, demoOpportunities);
    const customer = (await listCustomers()).find((item) => item.id === input.customer_id);
    const createdAt = new Date().toISOString();
    const next = { id: crypto.randomUUID(), owner_name: "演示管理员", created_at: createdAt, updated_at: createdAt, ...input, owner_id: input.owner_id || "demo-user", customer: customer ? { company_name: customer.company_name, contact_name: customer.contact_name } : undefined } as Opportunity;
    rows.unshift(next); saveLocal(OPPORTUNITY_KEY, rows); return next;
  }
  const { data: authData } = await supabase.auth.getUser();
  const { data, error } = await supabase.from("opportunities").insert({ ...input, owner_id: authData.user?.id, created_by: authData.user?.id }).select("*, customers(company_name, contact_name)").single();
  if (error) throw dataError(error, "商机暂时无法保存，请稍后重试");
  return { ...data, customer: data.customers } as Opportunity;
}

export async function updateOpportunity(id: string, input: Partial<OpportunityInput>): Promise<Opportunity> {
  if (!isSupabaseConfigured || !supabase) {
    const rows = loadLocal(OPPORTUNITY_KEY, demoOpportunities);
    const index = rows.findIndex((row) => row.id === id);
    if (index < 0) throw new Error("商机不存在");
    const before = rows[index];
    rows[index] = { ...before, ...input, updated_at: new Date().toISOString() };
    saveLocal(OPPORTUNITY_KEY, rows);
    if (before.stage !== rows[index].stage) {
      const audits = loadLocal(AUDIT_KEY, demoAuditLogs);
      audits.unshift({ id: crypto.randomUUID(), actor_id: "demo-user", actor_name: "演示管理员", action: "opportunity.stage_change", entity_type: "opportunities", entity_id: id, customer_id: rows[index].customer_id, details: { from: before.stage, to: rows[index].stage, summary: `商机阶段从 ${before.stage} 移动到 ${rows[index].stage}` }, created_at: rows[index].updated_at });
      saveLocal(AUDIT_KEY, audits);
    }
    return rows[index];
  }
  const { data: authData } = await supabase.auth.getUser();
  const { data, error } = await supabase.from("opportunities").update({ ...input, updated_by: authData.user?.id }).eq("id", id).select("*,customer:customers(company_name, contact_name),owner:profiles!opportunities_owner_id_fkey(full_name)").single();
  if (error) throw dataError(error, "商机暂时无法更新，请稍后重试");
  const row = data as unknown as Opportunity & { owner?: { full_name?: string | null } | null };
  return { ...row, owner_name: row.owner?.full_name || "业务员" };
}

export async function moveOpportunityStage(id: string, stage: Opportunity["stage"]): Promise<Opportunity> {
  if (!isSupabaseConfigured || !supabase) return updateOpportunity(id, { stage });
  const { data, error } = await supabase.rpc("move_opportunity_stage", { p_opportunity_id: id, p_stage: stage });
  if (error) throw dataError(error, "商机阶段暂时无法更新，请稍后重试");
  return data as Opportunity;
}

export async function deleteOpportunity(id: string): Promise<void> {
  if (!isSupabaseConfigured || !supabase) {
    const rows = loadLocal(OPPORTUNITY_KEY, demoOpportunities);
    const target = rows.find((row) => row.id === id);
    if (!target) throw new ApiError({ code: "OPPORTUNITY_NOT_FOUND", userMessage: "商机不存在或已删除", status: 404, retryable: false });
    saveLocal(OPPORTUNITY_KEY, rows.filter((row) => row.id !== id));
    const audits = loadLocal(AUDIT_KEY, demoAuditLogs);
    audits.unshift({ id: crypto.randomUUID(), actor_id: "demo-user", actor_name: "演示管理员", action: "opportunity.delete", entity_type: "opportunities", entity_id: id, customer_id: target.customer_id, details: { summary: `商机已软删除：${target.title}` }, created_at: new Date().toISOString() });
    saveLocal(AUDIT_KEY, audits); return;
  }
  const { data: authData } = await supabase.auth.getUser();
  const { error } = await supabase.from("opportunities").update({ deleted_at: new Date().toISOString(), updated_by: authData.user?.id }).eq("id", id);
  if (error) throw dataError(error, "商机暂时无法删除，请稍后重试", "OPPORTUNITY_DELETE_FAILED");
}

export async function listQuotes(customerId?: string): Promise<Quote[]> {
  if (!isSupabaseConfigured || !supabase) {
    const rows = loadLocal(QUOTE_KEY, demoQuotes);
    return customerId ? rows.filter((row) => row.customer_id === customerId) : rows;
  }
  let request = supabase.from("quotes").select("*,creator:profiles!quotes_created_by_fkey(full_name)").is("deleted_at", null).order("updated_at", { ascending: false }).limit(500);
  if (customerId) request = request.eq("customer_id", customerId);
  const { data, error } = await request;
  if (error) throw dataError(error, "报价数据暂时无法加载，请稍后重试");
  return (data ?? []).map((raw) => {
    const row = raw as unknown as Quote & { creator?: { full_name?: string | null } | null };
    return { ...row, creator_name: row.creator?.full_name || "业务员" };
  });
}

export async function createQuote(input: QuoteInput): Promise<Quote> {
  if (!isSupabaseConfigured || !supabase) {
    const rows = loadLocal(QUOTE_KEY, demoQuotes);
    const createdAt = new Date().toISOString();
    const next: Quote = { id: crypto.randomUUID(), created_by: "demo-user", creator_name: "演示管理员", created_at: createdAt, updated_at: createdAt, ...input };
    rows.unshift(next); saveLocal(QUOTE_KEY, rows); return next;
  }
  const { data: authData } = await supabase.auth.getUser();
  const { data, error } = await supabase.from("quotes").insert({ ...input, created_by: authData.user?.id, updated_by: authData.user?.id, owner_id: input.owner_id || authData.user?.id }).select("*,creator:profiles!quotes_created_by_fkey(full_name)").single();
  if (error) throw dataError(error, "报价暂时无法保存，请稍后重试");
  const row = data as unknown as Quote & { creator?: { full_name?: string | null } | null };
  return { ...row, creator_name: row.creator?.full_name || "业务员" };
}

export async function updateQuote(id: string, input: Partial<QuoteInput>): Promise<Quote> {
  if (!isSupabaseConfigured || !supabase) {
    const rows = loadLocal(QUOTE_KEY, demoQuotes);
    const index = rows.findIndex((row) => row.id === id);
    if (index < 0) throw new Error("报价不存在");
    const before = rows[index];
    rows[index] = { ...rows[index], ...input, updated_at: new Date().toISOString() };
    saveLocal(QUOTE_KEY, rows);
    const audits = loadLocal(AUDIT_KEY, demoAuditLogs);
    audits.unshift({ id: crypto.randomUUID(), actor_id: "demo-user", actor_name: "演示管理员", action: before.status === rows[index].status ? "quote.update" : "quote.status_change", entity_type: "quotes", entity_id: id, customer_id: rows[index].customer_id, details: { from: before.status, to: rows[index].status, summary: before.status === rows[index].status ? `更新报价：${rows[index].quote_no}` : `报价状态从 ${before.status} 调整为 ${rows[index].status}` }, created_at: rows[index].updated_at });
    saveLocal(AUDIT_KEY, audits); return rows[index];
  }
  const { data: authData } = await supabase.auth.getUser();
  const { data, error } = await supabase.from("quotes").update({ ...input, updated_by: authData.user?.id }).eq("id", id).select("*,creator:profiles!quotes_created_by_fkey(full_name)").single();
  if (error) throw dataError(error, "报价暂时无法更新，请稍后重试");
  const row = data as unknown as Quote & { creator?: { full_name?: string | null } | null };
  return { ...row, creator_name: row.creator?.full_name || "业务员" };
}

export async function deleteQuote(id: string): Promise<void> {
  if (!isSupabaseConfigured || !supabase) {
    const rows = loadLocal(QUOTE_KEY, demoQuotes);
    const target = rows.find((row) => row.id === id);
    if (!target) throw new ApiError({ code: "QUOTE_NOT_FOUND", userMessage: "报价不存在或已删除", status: 404, retryable: false });
    saveLocal(QUOTE_KEY, rows.filter((row) => row.id !== id));
    const audits = loadLocal(AUDIT_KEY, demoAuditLogs);
    audits.unshift({ id: crypto.randomUUID(), actor_id: "demo-user", actor_name: "演示管理员", action: "quote.delete", entity_type: "quotes", entity_id: id, customer_id: target.customer_id, details: { summary: `报价已软删除：${target.quote_no}` }, created_at: new Date().toISOString() });
    saveLocal(AUDIT_KEY, audits); return;
  }
  const { data: authData } = await supabase.auth.getUser();
  const { error } = await supabase.from("quotes").update({ deleted_at: new Date().toISOString(), updated_by: authData.user?.id }).eq("id", id);
  if (error) throw dataError(error, "报价暂时无法删除，请稍后重试", "QUOTE_DELETE_FAILED");
}

export async function listAttachments(customerId: string): Promise<Attachment[]> {
  if (!isSupabaseConfigured || !supabase) return loadLocal<Attachment[]>(ATTACHMENT_KEY, []).filter((row) => row.customer_id === customerId);
  const { data, error } = await supabase.from("attachments").select("*").eq("customer_id", customerId).is("deleted_at", null).order("created_at", { ascending: false });
  if (error) throw dataError(error, "附件列表暂时无法加载，请稍后重试");
  return (data ?? []) as Attachment[];
}

const allowedAttachmentTypes = new Set(["application/pdf", "application/msword", "application/vnd.openxmlformats-officedocument.wordprocessingml.document", "image/jpeg", "image/png"]);

export async function uploadCustomerAttachment(customerId: string, file: File): Promise<Attachment> {
  if (file.size < 1 || file.size > 5 * 1024 * 1024) throw new Error("附件必须小于 5MB 且不能为空");
  if (!allowedAttachmentTypes.has(file.type)) throw new Error("仅允许 PDF、Word、JPG 或 PNG 文件");
  const extension = file.name.includes(".") ? `.${file.name.split(".").pop()!.toLowerCase().replace(/[^a-z0-9]/g, "")}` : "";
  const objectPath = `${customerId}/${crypto.randomUUID()}${extension}`;
  if (!isSupabaseConfigured || !supabase) {
    const rows = loadLocal<Attachment[]>(ATTACHMENT_KEY, []);
    const next: Attachment = { id: crypto.randomUUID(), customer_id: customerId, bucket: "customer-attachments", object_path: objectPath, original_name: file.name, mime_type: file.type, size_bytes: file.size, uploaded_by: "demo-user", created_at: new Date().toISOString() };
    rows.unshift(next); saveLocal(ATTACHMENT_KEY, rows); return next;
  }
  const { data: authData } = await supabase.auth.getUser();
  if (!authData.user) {
    await expireSession();
    throw sessionExpiredError("Attachment upload found no authenticated user");
  }
  const upload = await supabase.storage.from("customer-attachments").upload(objectPath, file, { contentType: file.type, upsert: false });
  if (upload.error) throw dataError(upload.error, "附件暂时无法上传，请稍后重试", "ATTACHMENT_UPLOAD_FAILED");
  const { data, error } = await supabase.from("attachments").insert({ customer_id: customerId, bucket: "customer-attachments", object_path: objectPath, original_name: file.name, mime_type: file.type, size_bytes: file.size, uploaded_by: authData.user.id }).select("*").single();
  if (error) {
    await supabase.storage.from("customer-attachments").remove([objectPath]);
    throw dataError(error, "附件记录暂时无法保存，请稍后重试", "ATTACHMENT_METADATA_FAILED");
  }
  return data as Attachment;
}

export async function downloadAttachment(attachment: Attachment) {
  if (!isSupabaseConfigured) throw new Error("演示模式不保存文件正文");
  const result = await apiRequest<{ signedUrl: string }>("/api/attachments/sign", { method: "POST", body: JSON.stringify({ customerId: attachment.customer_id, bucket: attachment.bucket, objectPath: attachment.object_path }) });
  window.open(result.signedUrl, "_blank", "noopener,noreferrer");
}

function saveBlob(blob: Blob, fileName: string) {
  const url = URL.createObjectURL(blob);
  const anchor = document.createElement("a"); anchor.href = url; anchor.download = fileName; anchor.click();
  URL.revokeObjectURL(url);
}

export async function downloadCustomersCsv() {
  if (!isSupabaseConfigured || !supabase) {
    const headers = ["customer_no", "company_name", "contact_name", "country", "website", "email", "phone", "lead_source", "status", "grade", "is_key_account", "last_follow_up_at", "next_follow_up_at", "created_at", "updated_at"] as const;
    const rows = await listCustomers();
    const csvCell = (value: unknown) => `"${String(value ?? "").replaceAll('"', '""')}"`;
    saveBlob(new Blob(["\uFEFF", headers.join(","), "\r\n", ...rows.map((row) => `${headers.map((header) => csvCell(row[header])).join(",")}\r\n`)], { type: "text/csv;charset=utf-8" }), "yky-customers-demo.csv");
    return;
  }
  saveBlob(await apiBlob("/api/exports/customers.csv"), `yky-customers-${new Date().toISOString().slice(0, 10)}.csv`);
}

export async function downloadProductsCsv() {
  const result = await listProductsPage({ page: 1, pageSize: 100, sort: "product_no", order: "asc" });
  const headers = ["product_no", "name", "english_name", "category", "specification", "capacity", "unit", "default_moq", "sample_cycle_days", "default_packaging", "efficacy", "formula_type", "supplier_info", "status"] as const;
  const csvCell = (value: unknown) => `"${String(value ?? "").replaceAll('"', '""')}"`;
  saveBlob(new Blob(["\uFEFF", headers.join(","), "\r\n", ...result.rows.map((row) => `${headers.map((header) => csvCell(row[header])).join(",")}\r\n`)], { type: "text/csv;charset=utf-8" }), `yky-products-${new Date().toISOString().slice(0, 10)}.csv`);
}

export async function listAuditLogs(customerId?: string): Promise<AuditLog[]> {
  if (!isSupabaseConfigured || !supabase) {
    const rows = loadLocal(AUDIT_KEY, demoAuditLogs);
    return customerId ? rows.filter((row) => row.customer_id === customerId) : rows;
  }
  let request = supabase.from("audit_logs").select("id,actor_id,action,entity_type,entity_id,customer_id,details,created_at,actor:profiles!audit_logs_actor_id_fkey(full_name)").order("created_at", { ascending: false }).limit(500);
  if (customerId) request = request.eq("customer_id", customerId);
  const { data, error } = await request;
  if (error) throw dataError(error, "审计日志暂时无法加载，请稍后重试");
  return (data ?? []).map((row: Record<string, unknown>) => ({ ...row, actor_name: (row.actor as { full_name?: string } | null)?.full_name || "系统" })) as AuditLog[];
}

export async function listCustomerTimeline(customerId: string): Promise<TimelineEvent[]> {
  const [followUps, samples, quotes, opportunities, reminders, auditLogs, attachments] = await Promise.all([
    listFollowUps(customerId),
    listSamples(customerId),
    listQuotes(customerId),
    listOpportunities(customerId),
    listReminders(customerId),
    listAuditLogs(customerId),
    listAttachments(customerId),
  ]);
  return buildCustomerTimeline({ followUps, samples, quotes, opportunities, reminders, auditLogs, attachments });
}

export async function downloadAuditCsv() {
  const rows = await listAuditLogs();
  const cell = (value: unknown) => `"${String(value ?? "").replaceAll('"', '""')}"`;
  const csv = ["created_at,actor,action,entity_type,entity_id,summary", ...rows.map((row) => [row.created_at, row.actor_name, row.action, row.entity_type, row.entity_id, row.details.summary || ""].map(cell).join(","))].join("\r\n");
  saveBlob(new Blob(["\uFEFF", csv], { type: "text/csv;charset=utf-8" }), `yky-audit-${new Date().toISOString().slice(0, 10)}.csv`);
}

export async function getUsageMetrics(): Promise<UsageMetrics> {
  if (!isSupabaseConfigured || !supabase) {
    return { ...demoUsage, customer_count: loadDemoCustomers().length, follow_up_count: loadLocal(FOLLOW_UP_KEY, demoFollowUps).length };
  }
  const { data, error } = await supabase.from("free_usage_metrics").select("*").single();
  if (error) throw dataError(error, "免费额度数据暂时无法加载，请稍后重试");
  return data as UsageMetrics;
}

export { apiRequest };
